// utils/file.js
/* -------------------------------------------------------------------------- */
/*                               Subir archivos                               */
/* -------------------------------------------------------------------------- */
import { api } from "../../services/api";
import * as XLSX from "xlsx";

/**
 * Límite máximo por archivo individual (25 MB).
 * Previene que el navegador intente subir archivos excesivamente pesados
 * antes de viajar por la red.
 */
export const MAX_FILE_SIZE_BYTES = 25 * 1024 * 1024;

/**
 * Formatea bytes en una cadena legible (ej: "1.5 MB", "500 KB").
 *
 * @param {number|null} bytes
 * @returns {string}
 */
export function formatFileSize(bytes) {
    if (!bytes || isNaN(bytes)) return "";
    const units = ["B", "KB", "MB", "GB"];
    let size = bytes;
    let unitIndex = 0;
    while (size >= 1024 && unitIndex < units.length - 1) {
        size /= 1024;
        unitIndex++;
    }
    return `${size.toFixed(1)} ${units[unitIndex]}`;
}

/**
 * CONFIGURACIÓN CENTRALIZADA DE EXTENSIONES PERMITIDAS (FRONTEND)
 * Actualmente limitado únicamente a archivos PDF (.pdf).
 *
 * PARA CORREGIR O HABILITAR MÁS FORMATOS EN EL FUTURO:
 * Solo descomentar o añadir la extensión deseada a este arreglo.
 * Automáticamente actualizará las validaciones del cliente y el filtro
 * del explorador de archivos en el formulario.
 */
export const EXTENSIONES_ADJUNTOS_CONFIG = [
    ".pdf",
    // Extensiones posibles preparadas para habilitar si se requiere:
    // ".xlsx",
    // ".xls",
    // ".csv",
    // ".png",
    // ".jpg",
    // ".jpeg",
    // ".doc",
    // ".docx",
];

/**
 * Atributo 'accept' para inputs de tipo file (ej: ".pdf")
 */
export const ACCEPT_ADJUNTOS_INPUT = EXTENSIONES_ADJUNTOS_CONFIG.join(",");

/**
 * Valida si un nombre de archivo tiene una extensión permitida actualmente.
 * @param {string} fileName
 * @returns {boolean}
 */
export const esExtensionPermitida = (fileName = "") => {
    const ext = "." + (fileName.split(".").pop() || "").toLowerCase();
    return EXTENSIONES_ADJUNTOS_CONFIG.includes(ext);
};

/**
 * Procesa un archivo individual (conservado para retrocompatibilidad).
 *
 * @param {File} file
 * @param {Object} options
 * @param {boolean} options.readColumnsOnly - Si es true, solo lee columnas del Excel
 * @param {boolean} options.readOnly - Si es true, no sube y devuelve el objeto File en memoria
 * @returns {Promise<{path?: string, file: File, columns?: Array<Object>}>}
 */
export async function handleFile(file, options = {}) {
    try {
        // Si es Excel y queremos leer columnas
        if (options.readColumnsOnly && /\.(xlsx|xls|csv)$/i.test(file.name)) {
            const data = await file.arrayBuffer();
            const workbook = XLSX.read(data);
            const sheetName = workbook.SheetNames[0];
            const worksheet = workbook.Sheets[sheetName];
            const jsonData = XLSX.utils.sheet_to_json(worksheet, { defval: "" });

            return { file, columns: jsonData };
        }

        // Validar antes de decidir si se sube o se conserva temporalmente.
        if (!esExtensionPermitida(file.name)) {
            throw new Error(`El archivo '${file.name}' no tiene un formato permitido. Formatos aceptados: ${EXTENSIONES_ADJUNTOS_CONFIG.join(", ")}`);
        }

        if (file.size > MAX_FILE_SIZE_BYTES) {
            throw new Error(`El archivo '${file.name}' excede el límite máximo de 25 MB.`);
        }

        if (options.readOnly) {
            return { file }; // se guarda en memoria hasta confirmar el formulario
        }

        // Subir archivo (caso normal)
        const formData = new FormData();
        formData.append("file", file);
        const response = await api.upload(formData);

        if (!response?.path) throw new Error("No se recibió la ruta del archivo");
        return { path: response.path, file, filename: response.filename };
    } catch (error) {
        console.error("Error procesando archivo:", error);
        throw error;
    }
}

/**
 * Procesa y sube una lista de archivos concurrentemente.
 *
 * JUSTIFICACIÓN:
 * - Valida previamente cada archivo en el cliente (tipo y tamaño) para no enviar
 *   solicitudes condenadas a fallar en el servidor.
 * - Utiliza `Promise.all` para ejecutar las cargas de forma asíncrona, maximizando el
 *   ancho de banda disponible del usuario.
 *
 * @param {File[]} fileList - Lista de objetos File de Javascript
 * @returns {Promise<Array<{path: string, nombreOriginal: string, tamano: number, mimeType: string}>>}
 */
export async function handleMultipleFiles(fileList) {
    if (!Array.isArray(fileList) || fileList.length === 0) return [];

    // 1. Validaciones previas en cliente
    for (const f of fileList) {
        if (!esExtensionPermitida(f.name)) {
            throw new Error(`El archivo '${f.name}' no tiene un formato permitido. Formatos aceptados: ${EXTENSIONES_ADJUNTOS_CONFIG.join(", ")}`);
        }
        if (f.size > MAX_FILE_SIZE_BYTES) {
            throw new Error(`El archivo '${f.name}' supera el límite permitido de 25 MB.`);
        }
    }

    // 2. Subida concurrente de cada archivo
    const uploadPromises = fileList.map(async (file) => {
        const formData = new FormData();
        formData.append("file", file);
        const response = await api.upload(formData);

        if (!response?.path) {
            throw new Error(`No se recibió respuesta válida para '${file.name}'.`);
        }

        return {
            path: response.path,
            nombreOriginal: file.name,
            tamano: file.size,
            mimeType: file.type || "application/pdf",
        };
    });

    return await Promise.all(uploadPromises);
}

/* -------------------------------------------------------------------------- */
/*                           Acceder a los archivos                           */
/* -------------------------------------------------------------------------- */
/**
 * Abre o descarga un archivo remoto desde el servidor.
 *
 * @param {string} path - Ruta pública (/uploads/...)
 * @param {string} [customName] - Nombre con el que se desea descargar
 */
export async function handleFileAccess(path, customName) {
    const backendUrl = process.env.REACT_APP_SERVER_URL;
    const downloadName = customName || path.split("/").pop();

    if (path.toLowerCase().endsWith(".pdf")) {
        window.open(`${backendUrl}${path}`, "_blank"); // Abre PDF directamente en visor del navegador
    } else {
        fetch(`${backendUrl}${path}`)
            .then((res) => res.blob())
            .then((blob) => {
                const url = window.URL.createObjectURL(blob);
                const a = document.createElement("a");
                a.href = url;
                a.download = downloadName;
                document.body.appendChild(a);
                a.click();
                a.remove();
                window.URL.revokeObjectURL(url);
            })
            .catch((err) => console.error("Error al obtener archivo", err));
    }
}

